import { Suspense, lazy } from 'react';
import * as Sentry from '@sentry/react';
import { ConfigProvider, theme } from 'antd';
import enGB from 'antd/es/locale/en_GB';
import nbNO from 'antd/locale/nb_NO';
import dayjs from 'dayjs';

import 'dayjs/locale/en-gb';
import updateLocale from 'dayjs/plugin/updateLocale';
import { MapProvider } from 'react-map-gl';
import { Outlet } from 'react-router-dom';
import { useDarkMode } from 'usehooks-ts';

import {
  AppProvider,
  AuthProvider,
  ProfileProvider,
  WorkspaceProvider,
  useLocale,
} from '@/lib/contexts';
import { Error } from '@/pages/error';

dayjs.extend(updateLocale);
dayjs.updateLocale('en_gb', {
  weekStart: 0,
});

const Modals = lazy(() => import('../../modals'));
const Toaster = lazy(() => import('../../toaster'));

export function AppLayout() {
  const { isDarkMode } = useDarkMode();
  const { locale } = useLocale();
  const localeAntd = locale === 'nb' ? nbNO : enGB;

  return (
    <Sentry.ErrorBoundary fallback={<Error />}>
      <AuthProvider>
        <ProfileProvider>
          <AppProvider>
            <ConfigProvider
              locale={localeAntd}
              theme={{
                algorithm: isDarkMode
                  ? theme.darkAlgorithm
                  : theme.defaultAlgorithm,
                token: {
                  // Seed Token
                  colorPrimary: '#4a9b8f',
                  colorBgBase: isDarkMode ? '#070608' : '#fff',
                  borderRadius: 4,
                  fontFamily: 'Untitled Sans',
                },
              }}
            >
              <WorkspaceProvider>
                <MapProvider>
                  <Outlet />
                  <Suspense fallback={null}>
                    <Modals />
                  </Suspense>
                  <Suspense fallback={null}>
                    <Toaster />
                  </Suspense>
                </MapProvider>
              </WorkspaceProvider>
            </ConfigProvider>
          </AppProvider>
        </ProfileProvider>
      </AuthProvider>
    </Sentry.ErrorBoundary>
  );
}

export default AppLayout;
